import dayjs from "dayjs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loading } from "../../components";
import { SERVICE_STATE_LABEL } from "../../constants/service";
import { auth } from "../../firebase";
import { useDpcs, useUserPartnerInformation } from "../../hooks";
import { ServiceState } from "../../types";
import { DpcFilters } from "./DpcFilters";
import { DpcTableHead } from "./DpcTableHead";
import { FilterProperty } from "./types";

const TABLE_ROW_HEIGHT = 60;
const TABLE_HEAD_MARGIN = 12;

const Dpc = () => {
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  const [user] = useAuthState(auth);

  const { userPartnerInformation } = useUserPartnerInformation(user?.uid);

  const { dpcs, isLoading } = useDpcs(userPartnerInformation?.data?.partnerId);



  const location = useLocation();
  const navigate = useNavigate();

  const [filterProperty, setFilterProperty] = useState<FilterProperty | null>(
    null
  );

  const [filterValue, setFilterValue] = useState<ReactNode | null>(null);

  const filterByValueOptions = useMemo(() => {
    if (!filterProperty) {
      return [];
    }

    const filterValues = dpcs?.data
      ?.map((item) => item[filterProperty])
      .filter((item) => item && item !== "N/A");

    const uniqueFilterValues = [...new Set(filterValues)];

    return uniqueFilterValues.map((item) => ({
      label:
        filterProperty === FilterProperty.ServiceState
          ? SERVICE_STATE_LABEL[item as ServiceState]
          : item,
      onClick: () => setFilterValue(item),
    }));
  }, [dpcs, filterProperty]);

  const [page, setPage] = useState(0);

  const filteredDpcs = useMemo(() => {
    if (!filterProperty || (filterProperty && !filterValue)) {
      return dpcs?.data;
    }

    return dpcs?.data?.filter((dpc) => dpc[filterProperty] === filterValue);
  }, [filterProperty, filterValue, dpcs]);

  const [tableHeight, setTableHeight] = useState(0);

  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!tableRef?.current) {
      return;
    }

    setTableHeight(tableRef.current.clientHeight);
  }, [tableRef]);

  const rowsPerPage = useMemo(
    () =>
      Math.max(
        1,
        Math.floor(
          (tableHeight - TABLE_ROW_HEIGHT - TABLE_HEAD_MARGIN) /
          TABLE_ROW_HEIGHT
        )
      ),
    [tableHeight]
  );

  const totalPages = useMemo(
    () => Math.ceil((filteredDpcs?.length || 0) / rowsPerPage),
    [filteredDpcs, rowsPerPage]
  );

  const paginatedDpcs = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredDpcs?.slice(start, end);
  }, [page, rowsPerPage, filteredDpcs]);

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage((prevPage) => prevPage - 1);
    }
  };

  return (
    <>
      <div className="flex flex-col px-10 h-[calc(100vh-60px)] overflow-hidden">
        <div className="flex justify-between items-center mb-3 mt-12">
          <h1 className="text-xl font-bold me-2">Customer Base</h1>
        </div>
        <div className="flex w-full justify-between">
          {(dpcs?.data ?? []).length > 0 && (
            <DpcFilters
              filterProperty={filterProperty}
              filterValue={filterValue}
              onFilterValueClick={(value) => setFilterValue(value)}
              onFilterPropertyClick={(property) => setFilterProperty(property)}
              filterByValueOptions={filterByValueOptions}
            />
          )}
          {totalPages > 1 && (
            <div className="flex gap-2 items-center">
              <button
                className="h-6 w-6 bg-gray-300 rounded-full flex items-center justify-center disabled:opacity-50"
                disabled={page === 0}
                onClick={handlePrevPage}
              >
                <ChevronLeft className="h-4 w-4 stroke-slate-500" />
              </button>
              <p className="text-sm">
                Page {page + 1} of {totalPages}
              </p>
              <button
                className="h-6 w-6 bg-gray-300 rounded-full flex items-center justify-center disabled:opacity-50"
                disabled={page + 1 === totalPages}
                onClick={handleNextPage}
              >
                <ChevronRight className="h-4 w-4 stroke-slate-500" />
              </button>
            </div>
          )}
        </div>

        <div className="flex-grow overflow-auto" ref={tableRef}>
          <div className="min-w-full inline-block align-middle pb-4">
            <div className="border rounded-lg overflow-hidden">
              <table className="leading-normal mt-3 w-full">
                <DpcTableHead />
                <tbody>
                  {paginatedDpcs?.length ? (
                    paginatedDpcs.map((item, index) => {
                      return (
                        <tr
                          key={index}
                          role="row"
                          onClick={() => navigate(`/dpc/${item.dpcId}`, { state: { previousLocation: location } })}
                          className="cursor-pointer table-row bg-white border-b h-[60px] text-start align-middle border-gray-200 hover:bg-slate-50 transition-colors"
                        >
                          <td className="text-sm pl-5 py-3 whitespace-nowrap">
                            <p className="text-gray-900">
                              {item.orderNumber}
                            </p>
                          </td>
                          <td className="text-sm pl-5 py-3 whitespace-nowrap">
                            <p className="text-gray-900">
                              {item.sku}
                            </p>
                          </td>
                          <td className="text-sm pl-5 py-3 whitespace-nowrap">
                            <p className="text-gray-900">
                              {item.productName}
                            </p>
                          </td>
                          <td className="text-sm pl-5 py-3 whitespace-nowrap">
                            <p className="text-gray-900">
                              {item.location}
                            </p>
                          </td>
                          <td className="text-sm pl-5 py-3 whitespace-nowrap">
                            <p className="text-gray-900">
                              {item.category}
                            </p>
                          </td>
                          <td className="text-sm pl-5 py-3 whitespace-nowrap">
                            <p className="text-gray-900">
                              {item.condition}
                            </p>
                          </td>
                          <td className="text-sm pl-5 py-3 whitespace-nowrap">
                            <p className="text-gray-900">
                              {item.warrantyEndDate
                                ? dayjs(item.warrantyEndDate).format("MMM YYYY")
                                : "None"}
                            </p>
                          </td>
                          <td className="text-sm pl-5 py-3 whitespace-nowrap">
                            <p className="text-gray-900">
                              {SERVICE_STATE_LABEL[item.serviceState]}
                            </p>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={8} // 8 columns in DpcTableHead
                        className="text-center py-4 text-sm text-gray-500"
                      >
                        No customers found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {isLoading && <Loading floating />}
    </>
  );
};

export default Dpc;
